import type { Styles } from '@adornis/ass/style.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { selectionSet, type BaseQLSelectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flat-loader.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import { impersonatingUser } from '@adornis/users/client/currentUser.js';
import { subscribeMyTermins } from '@ils/evaluation-scheduler/api/EvaluationTermin.js';
import '@ils/evaluation-scheduler/client/ils-evaluation-termin-card.js';
import { EvaluationTermin } from '@ils/evaluation-scheduler/db/EvaluationTermin.js';
import { getNextSlotForEvaluation, getSlotForRating, getSlotsWithoutKonsens } from '@ils/ratings/api/evaluation.js';
import { getCommittedRatingsOlderThanXDays, getUncommittedRating } from '@ils/ratings/api/ratings.js';
import type { InspectorOverviewLine } from '@ils/ratings/db/inspectorOverviewLine.js';
import type { Slot } from '@ils/slots/db/slot.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { filter, map, of, switchMap } from 'rxjs';
import { subscribeActiveLivekitRooms } from '../../../db/livekit.js';

const slotSelector = {
  _id: 1,
  osce: { _id: 1, title: 1, titleTrainee: 1, osceType: 1, enableVideo: 1, conversationType: 1, taskType: 1 },
  roomID: 1,
  startTime: 1,
} as const satisfies BaseQLSelectionSet<Slot>;

const inspectorLineSelector = {
  slot: slotSelector,
  coInspector: { _id: 1, ecommerce: { firstName: 1, lastName: 1 } },
} as const satisfies BaseQLSelectionSet<InspectorOverviewLine>;

type NarrowSlot = NarrowedEntity<Slot, typeof slotSelector>;

type NarrowInspectorOverviewLine = NarrowedEntity<InspectorOverviewLine, typeof inspectorLineSelector>;

@customElement('ils-evaluation-overview-inspector')
export class ILSEvaluationOverviewInspector extends ChemistryLitElement {
  @state() private readonly _uncommittedRating = new RXController(this, getUncommittedRating()({ _id: 1 }));
  @state() private readonly _uncommittedRatingSlot = new RXController(
    this,
    this._uncommittedRating.observable.pipe(
      filter(Boolean),
      switchMap(rating => getSlotForRating(rating._id)(slotSelector) as any as Promise<NarrowSlot>),
    ),
  );

  @state() private readonly _nextSlot = new RXController(
    this,
    this._uncommittedRating.observable.pipe(
      switchMap(rating => {
        if (rating || rating === undefined) return of(null);
        return getNextSlotForEvaluation()(slotSelector) as any as Promise<NarrowSlot>;
      }),
    ),
  );

  @state() private readonly _slotsWithoutKonsens = new RXController(
    this,
    getSlotsWithoutKonsens()(inspectorLineSelector) as any as Promise<NarrowInspectorOverviewLine[]>,
  );

  @state() private readonly _myTermins = new RXController(
    this,
    subscribeMyTermins()(selectionSet(() => EvaluationTermin, 2)).pipe(
      filter(Boolean),
      map(termins => termins.filter(termin => termin.end >= DateTime.now())),
    ),
  );

  @state() private readonly _oldRatingsWithoutKonsens = new RXController(
    this,
    this._myTermins.observable.pipe(
      switchMap(async termins => {
        const oldRatings = await getCommittedRatingsOlderThanXDays(7)({
          _id: 1,
          updatedAt: 1,
          slotID: 1,
          createdAt: 1,
        });
        oldRatings.sort((a, b) => b.updatedAt.toMillis() - a.updatedAt.toMillis());
        if (!termins) return oldRatings;
        const scheduledCount = termins.reduce((count, termin) => count + termin.slotCount, 0);
        return oldRatings.slice(scheduledCount);
      }),
    ),
  );

  @state() private readonly _activeRoomIDs = new RXController(
    this,
    subscribeActiveLivekitRooms()({ name: 1 }).pipe(map(rooms => rooms.map(room => room.name))),
  );

  @state() private readonly _impersonatingUser = new RXController(this, impersonatingUser());

  override render() {
    if (this._slotsWithoutKonsens.isLoading || this._myTermins.isLoading || this._uncommittedRating.isLoading)
      return this._loader('');

    return html`
      <x-section space="md" h100 padding="md">
        ${['development', 'qa'].includes(baseConfig.get('DEPLOYMENT')) || this._impersonatingUser.value
          ? html`
              <x-button
                mode="text"
                icon="search"
                ${css({ alignSelf: 'end' })}
                @click=${() => this._openRatingBySlotID()}
              >
                Öffne Bewertung via SlotID &lt;DEV&gt;
              </x-button>
            `
          : nothing}
        ${this._uncommittedRatingSlot.value ? this._uncommittedRatingDoc() : this._renderNextSlot()}

        <!-- termine -->
        ${this._termins()}
        <!-- evaluation rating docs -->
        ${this._evaluationRatingDocs()}
      </x-section>
    `;
  }
  private async _openRatingBySlotID() {
    const slotID = await XNativeDialog.prompt(
      html`SlotID zu bewerten. <br />
        <br />
        !!! Mehrere verschiedene Bewertungen gleichzeitig zu starten und nicht zu beenden führt dazu <br />
        dass diese nacheinander als "Bewertung fortsetzen" angezeigt werden, bevor "Als nächstes zu bewerten" <br />
        wieder angezeigt wird. <br />
        <br />
        SlotIDs können aus der Admin Bewertungsübersicht kopiert werden.`,
    );
    if (!slotID) return;
    const inspectorLines = this._slotsWithoutKonsens.value ?? [];
    if (inspectorLines.some(line => line.slot._id === slotID)) {
      goTo({ path: `/start/evaluation-sync/${slotID}`, searchParams: NO_CHANGE });
    } else {
      goTo({ path: `/start/evaluation-async/${slotID}`, searchParams: NO_CHANGE });
    }
  }

  private _evaluationRatingDocs() {
    const inspectorLines = this._slotsWithoutKonsens.value ?? [];
    if (inspectorLines.length === 0) return nothing;
    return html`
      <x-flex space="sm">
        <x-h3> Für Konsens ausstehende Bewertungen </x-h3>
        ${repeat(
          inspectorLines,
          line => line.slot._id,
          line =>
            this._slotCard({
              slot: line.slot,
              coInspector: line.coInspector,
              icon: 'wifi',
              tooltip: 'Bewertungsraum betreten',
              readyForConsens: true,
            }),
        )}
      </x-flex>
      <x-hr></x-hr>
    `;
  }

  private _termins() {
    const myTermins = this._myTermins.value ?? [];

    if (myTermins.length === 0) return nothing;

    return html`
      <x-flex space="sm">
        <x-h3> Deine nächsten Termine </x-h3>
        <x-grid columns="repeat(auto-fill, minmax(260px, 1fr))" space="md">
          ${repeat(
            myTermins,
            termin => termin._id,
            termin => html` <ils-evaluation-termin-card .termin=${termin}></ils-evaluation-termin-card> `,
          )}
        </x-grid>
      </x-flex>
      <x-hr></x-hr>
    `;
  }

  private _renderNextSlot() {
    const content = () => {
      if (this._nextSlot.isLoading)
        return html`
          <x-flat-loader></x-flat-loader>
          <x-text> Suche nach offenen Bewertungen... </x-text>
        `;
      const nextSlot = this._nextSlot.value;
      const osce = nextSlot?.osce;

      if (!nextSlot) return html` <x-text> Du hast keine offenen Bewertungen. </x-text> `;
      if (!osce) return html` <x-text> Aufgabenstellung konnte nicht gefunden werden </x-text> `;
      return this._slotCard({ slot: nextSlot });
    };

    return html`
      <x-flex space="sm">
        <x-h3> Als nächstes zu bewerten </x-h3>
        ${content()}
      </x-flex>
      <x-hr></x-hr>
    `;
  }

  private _uncommittedRatingDoc() {
    const slot = this._uncommittedRatingSlot.value;
    if (!slot) return html` slot not found `;

    return html`
      <x-flex space="md">
        <x-h3> Bewertung fortsetzen </x-h3>
        ${this._slotCard({ slot, tooltip: 'Bewertung fortsetzen', icon: 'pause' })}
      </x-flex>
      <x-hr></x-hr>
    `;
  }

  private _slotCard({
    slot,
    coInspector,
    tooltip = 'Bewertung starten',
    icon = 'play_arrow',
    readyForConsens = false,
  }: {
    slot: NarrowSlot;
    coInspector?: Maybe<{ _id: string; ecommerce: Maybe<{ firstName: string; lastName: string }> }>;
    tooltip?: string;
    icon?: string;
    readyForConsens?: boolean;
  }) {
    const hasActiveRoom = (this._activeRoomIDs.value ?? []).includes(slot.roomID);
    const oldRating = this._oldRatingsWithoutKonsens.value?.find(rating => rating.slotID === slot._id);

    return html`
      <x-flex
        space="sm"
        ${css({
          background: '#fff',
          borderRadius: '8px',
        })}
      >
        <x-flex horizontal crossaxis-center space="md" space-between>
          <x-flex
            horizontal
            crossaxis-center
            space="sm"
            padding="sm"
            ${css({
              background: oldRating ? this.colors.tone.error.shade(50) : 'unset',
              borderRadius: this.sizes.borderRadius,
            })}
          >
            <ils-osce-type .osce=${slot.osce}></ils-osce-type>
            <x-text> ${slot.osce?.title} </x-text>
            ${coInspector
              ? html`
                  <x-text tone="subtle">
                    ${coInspector.ecommerce?.firstName} ${coInspector.ecommerce?.lastName}
                  </x-text>
                `
              : nothing}
            ${['development', 'qa'].includes(baseConfig.get('DEPLOYMENT'))
              ? html`
                  <x-button
                    mode="text"
                    tone="subtle"
                    icon="content_copy"
                    @click=${async () => {
                      await navigator.clipboard.writeText(slot._id);
                      void XSnackbar.show('SlotID kopiert');
                    }}
                  >
                    <x-tooltip> <x-text> &lt;DEV&gt; Kopiere SlotID ${slot._id} </x-text> </x-tooltip>
                  </x-button>
                `
              : nothing}
            ${oldRating
              ? html`
                  <x-icon ${css({ color: this.colors.tone.error })}> history </x-icon>
                  <x-tooltip>
                    <x-text>
                      Diese Bewertung ist vor mehr als 7 Tagen abgeschlossen worden und wurde noch nicht verglichen. Du
                      hast die Bewertung das letzte mal am
                      ${oldRating.updatedAt.toLocaleString(DateTime.DATETIME_SHORT)} geändert.
                    </x-text>
                  </x-tooltip>
                `
              : nothing}
            <x-text tone="subtle"> ${slot.startTime.toLocaleString()}</x-text>
          </x-flex>
          <x-flex
            ${css({
              position: 'relative',
              padding: this.spacing.sm,
              borderRadius: '8px',
              cursor: 'pointer',
              border: `2px solid ${this.colors.accent}`,
              background: readyForConsens ? '#fff' : this.colors.accent,
            })}
            center
            crossaxis-center
            @click=${() => {
              if (readyForConsens) return goTo({ path: `/start/evaluation-sync/${slot._id}`, searchParams: NO_CHANGE });
              goTo({ path: `/start/evaluation-async/${slot._id}`, searchParams: NO_CHANGE });
            }}
          >
            ${readyForConsens
              ? html`
                  <x-flex
                    ${css({
                      position: 'absolute',
                      right: '-10px',
                      top: '-10px',
                      height: '18px',
                      width: '18px',
                      border: hasActiveRoom ? `4px solid #A6DBBF` : '4px solid #F3F4F3',
                      background: hasActiveRoom ? '#26E37F' : '#D0D1D0',
                      borderRadius: '50%',
                    })}
                  ></x-flex>
                `
              : nothing}

            <x-icon ${css({ color: readyForConsens ? this.colors.accent : '#000' })}> ${icon} </x-icon>
            <x-tooltip><x-text> ${tooltip} </x-text></x-tooltip>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  private _loader(text: string) {
    return html`
      <x-flex space="lg" w100 h100 center crossaxis-center> <ils-loader> </ils-loader> <x-h3> ${text} </x-h3> </x-flex>
    `;
  }

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        ':host': {
          height: '100%',
        },
      },
    ];
  }
}
